import {
	getAuthTokenFromSearchParams,
	type RampsSearchSchema,
	validateRampsSearch,
} from "@/lib/ramps-search-schema";
import {
	authTypeFromJwtSub,
	getPosthogDistinctIdFromJwt,
	getTokenType,
} from "@/utils/jwt-decoder";

export type PosthogAuthType = "custodial" | "non_custodial";

export interface StableDistinctIdResult {
	distinctId: string;
	auth_type: PosthogAuthType;
	/** True when distinctId came from JWT `sub` (or legacy `wallet:id`). */
	fromJwt: boolean;
}

interface ResolveStoreSlice {
	token?: string;
	walletAddress?: string;
}

/**
 * Resolves a stable PostHog distinct_id from URL search + store.
 * JWT: full `sub` (custodial compound or plain Stellar). v5: wallet from URL/store.
 */
export function resolveStableDistinctId(
	search: Record<string, unknown>,
	store: ResolveStoreSlice,
): StableDistinctIdResult | null {
	const validated: RampsSearchSchema = validateRampsSearch(search);
	const authToken =
		getAuthTokenFromSearchParams(validated) || store.token?.trim() || "";

	if (authToken && getTokenType(authToken) === "jwt") {
		const distinctId = getPosthogDistinctIdFromJwt(authToken);
		if (distinctId) {
			return {
				distinctId,
				auth_type: authTypeFromJwtSub(distinctId),
				fromJwt: true,
			};
		}
	}

	if (validated.accesstoken) {
		const wallet = (
			validated.walletAddress ||
			store.walletAddress ||
			""
		).trim();
		if (wallet) {
			return {
				distinctId: wallet,
				auth_type: "non_custodial",
				fromJwt: false,
			};
		}
	}

	const walletOnly = (
		validated.walletAddress ||
		store.walletAddress ||
		""
	).trim();
	if (walletOnly) {
		return {
			distinctId: walletOnly,
			auth_type: "non_custodial",
			fromJwt: false,
		};
	}

	return null;
}
